"use client";

import { useState, type FC, type Ref } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import {
  DocumentUploadField,
  type DocumentUploadFieldProps,
  type FileUploadFieldChangeHandler,
  Flex,
  FormLabel,
  InputAddOn,
  Message,
  Reveal,
  Spacing,
  Switch,
  Text,
  transitionDurations,
  type UploadedDocument,
  useUuid,
  useDebug,
} from "@huub-web/ui";
import { rem } from "@huub-web/utils";

import { FormTemplate, type FormTemplateProps } from "../FormTemplate";
import { type AllowedFileTypes, getAllowedFileMimeTypes } from "../../utils";
import { useTemplates } from "../../hooks/useTemplates";

/**
 * @deprecated 💀
 */
export interface DocumentPreFillFormTemplateProps {
  id: string;
  input: {
    data: Pick<DocumentUploadFieldProps, "placeholder" | "value"> & {
      accepted?: AllowedFileTypes[];
      endpoint?: string;
    };
  };
  form: {
    id: string;
    data: FormTemplateProps;
  };
  description?: string;
  manualLabel?: string;
  status?: DocumentPreFillFormUploadStatus;
  messages?: {
    processing?: string;
    processed?: string;
    uploadError?: string;
    proccessingError?: string;
  };
  open?: boolean;
}

export type DocumentPreFillFormUploadStatus =
  | "idle"
  | "uploading"
  | "uploadError"
  | "processing"
  | "processed"
  | "proccessingError";

/**
 * A template for rendering a documents upload component with conditional visibility and disabled states.
 * @deprecated 💀
 * @todo Multiple files uploads
 * @todo Delete file
 */
export const DocumentPreFillFormTemplate: FC<
  DocumentPreFillFormTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  id,
  input,
  form,
  description,
  messages,
  manualLabel,
  status: statusProp = "idle",
  open: openProp = false,
  ref,
}) => {
  const { t } = useTranslation();
  const { uploadDocument, lockUI } = useTemplates();
  const { log } = useDebug();

  const labelID = useUuid();
  const [value, setValue] = useState<UploadedDocument | null>(
    input?.data?.value ?? null,
  );

  const [status, setStatus] =
    useState<DocumentPreFillFormUploadStatus>(statusProp);

  const allowedTypes: string[] = getAllowedFileMimeTypes(
    input?.data.accepted ?? ["pdf", "doc", "image"],
  );

  const [isOpen, setIsOpen] = useState(openProp);

  const isFinalOpen =
    isOpen ||
    ["processing", "uploadError", "proccessingError", "processed"].includes(
      status,
    );

  const handleChange: FileUploadFieldChangeHandler = (files) => {
    if (files && files.length > 0) {
      log("purple", "DocumentPreFillFormTemplate", "handleChange", files);
      setValue({ name: files[0].name, status: "idle" });
      handleFileUpload(files[0]);
    } else {
      setStatus("idle");
      setValue(null);
    }
  };

  const handleFileUpload = async (file: File) => {
    log("purple", "DocumentsUploadTemplate", "handleFileUpload", file);

    setStatus("processing");

    const endpoint = input?.data?.endpoint;
    if (!endpoint) {
      setStatus("uploadError");
      throw new Error("Endpoint is required");
    }

    const response = await uploadDocument(endpoint, file);
    if (!response.success) {
      setValue(null);
      setStatus("uploadError");
      return;
    }

    setErrorMessage(undefined);
    setValue({ name: value?.name ?? file.name, status: "pending" });
  };

  const isLocked = lockUI || status === "processing" || status === "uploading";

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  // TODO: preFillFormStatus has been removed - this template is deprecated
  // useEffect(() => {
  //   const unsubscribe = pubSub.subscribe("preFillFormStatus", (action) => {
  //     if (action.id === id) {
  //       nextFrame(() => {
  //         // If file cannot be processed, the error is quicker then the actual HTTP request, so we need to wait a frame
  //         setStatus(
  //           action.status === "error" ? "proccessingError" : "processed",
  //         );
  //         setValue({ name: value?.name ?? "", status: "idle" });
  //         setErrorMessage(action.message ?? undefined);
  //       }, 10);
  //     }
  //   });

  //   return () => unsubscribe();
  // }, [pubSub, value]);

  return (
    <Flex id={id} ref={ref} direction="column" gap="m">
      {description && (
        <Text markdown style={{ maxWidth: rem(500) }}>
          {description}
        </Text>
      )}

      <DocumentUploadField
        placeholder={input?.data?.placeholder}
        loading={status === "uploading" || status === "processing"}
        disabled={isLocked || status === "processed"}
        value={value}
        onChange={(files) => handleChange(files)}
        allowedTypes={allowedTypes}
        leading={
          <InputAddOn icon="documentAI" variant="leading" iconSize="s2" />
        }
      />

      {/* <pre>
          {JSON.stringify(
            {
              id,
              isLocked,
              status,
              value,
            },
            null,
            2,
          )}
        </pre> */}

      <Reveal>
        {[
          "processing",
          "uploadError",
          "proccessingError",
          "processed",
        ].includes(status) && (
          <>
            <Message
              variant={
                ["uploadError", "proccessingError"].includes(status)
                  ? "error"
                  : status === "processed"
                    ? "success"
                    : "default"
              }
              body={
                !!errorMessage &&
                ["uploadError", "proccessingError"].includes(status)
                  ? errorMessage
                  : messages?.[
                      status as keyof DocumentPreFillFormTemplateProps["messages"]
                    ]
              }
            />
            <Spacing />
          </>
        )}
      </Reveal>

      {!["processing", "uploadError", "proccessingError", "processed"].includes(
        status,
      ) && (
        <div style={{ padding: `${rem(12)} 0` }}>
          <Flex align="center" gap="xs">
            <Switch
              disabled={isLocked}
              checked={isOpen}
              onChange={() => setIsOpen(!isOpen)}
              label={t("templating.preview")}
              id={labelID}
              variant="secondary"
            />
            <FormLabel htmlFor={labelID}>
              {manualLabel ?? t("documentPreFillForm.manualLabel")}
            </FormLabel>
          </Flex>
        </div>
      )}

      <AnimatePresence initial={false}>
        {isFinalOpen && (
          <motion.div
            // id={contentID}
            role="region"
            // aria-labelledby={buttonID}
            // className={contentStyle()}
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={{
              open: { opacity: 1, height: "auto" },
              collapsed: { opacity: 0, height: 0 },
            }}
            transition={{
              duration: transitionDurations.m,
              ease: "easeInOut",
            }}
          >
            {/* <div className={padderStyle({})}> */}
            <FormTemplate {...form?.data} disabled={isLocked} id={form?.id} />
            {/* </div> */}
          </motion.div>
        )}
      </AnimatePresence>
    </Flex>
  );
};
